import { useEffect, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GraduationCap, RefreshCw, Loader2, Flag } from "lucide-react";
import { toast } from "sonner";


/**
 * Detect whether a string contains any markdown syntax we care about.
 * Cheap heuristic — if none present, we render as a plain <p> to keep
 * the visual identical to legacy AI-drafted prose.
 */
const _HAS_MD = /(^|\n)(#{1,6}\s|[-*]\s|\d+\.\s)|\*\*[^*]+\*\*|_[^_]+_|\[[^\]]+\]\([^)]+\)/;

/**
 * AIInterpretationCard — "The Professor" reads each degree page.
 *
 * Fetches the cached interpretation on mount. If none exists, lazy-generates
 * on first view via POST /degrees/:id/interpret (Claude Sonnet 4.5).
 *
 * Two render modes:
 *   - variant="summary"  → the big top-of-page overview paragraph
 *   - variant="note"     → a compact italic Professor's note above a data
 *                          grid, keyed by `sectionKey` (admissions/cost/
 *                          curriculum/outcomes/international).
 *
 * All variants share the same fetch/state so we only ever fire ONE request
 * per DegreeDetail page load — the parent passes `interpretation` down.
 */
export function useAIInterpretation(degreeId) {
  const [state, setState] = useState({
    interpretation: null,
    loading: true,
    generating: false,
    error: null,
    inheritedFrom: null,
  });

  const load = useCallback(async () => {
    if (!degreeId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(`/degrees/${degreeId}/interpret`);
      if (data.cached && data.interpretation) {
        setState({
          interpretation: data.interpretation,
          loading: false,
          generating: false,
          error: null,
          inheritedFrom: data.inherited_from || null,
        });
        return;
      }
      // Not cached — lazy-generate. This is what makes the page feel warm on
      // first view (10-20s LLM call). We only fire once because POST is
      // idempotent (returns cached if a concurrent viewer already wrote it).
      setState((s) => ({ ...s, loading: false, generating: true }));

      // Silent auto-retry on 503 (Emergent free-tier concurrent-call limit).
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const { data: gen } = await api.post(`/degrees/${degreeId}/interpret`);
          setState({
            interpretation: gen.interpretation,
            loading: false,
            generating: false,
            error: null,
            inheritedFrom: gen.inherited_from || null,
          });
          return;
        } catch (err) {
          lastErr = err;
          if (err?.response?.status !== 503) break; // non-rate-limit — give up now
          if (attempt < 2) {
            await new Promise((r) => setTimeout(r, 8000));
          }
        }
      }
      throw lastErr;
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = detail || "The Professor is unavailable right now.";
      if (status === 503) {
        msg = "The Professor is helping other readers right now — tap retry in a few seconds.";
      }
      setState({
        interpretation: null,
        loading: false,
        generating: false,
        error: msg,
        inheritedFrom: null,
      });
    }
  }, [degreeId]);

  useEffect(() => { load(); }, [load]);

  return { ...state, reload: load };
}

export function AIInterpretationSummary({ interpretation, loading, generating, error, reload, degreeId, inheritedFrom }) {
  const { user } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const isAdmin = !!user?.is_admin;

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/degrees/${degreeId}/interpret/refresh`);
      toast.success("The Professor has revised the summary.");
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed.");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading || generating) {
    return (
      <div
        data-testid="ai-interpretation-loading"
        className="mt-10 p-8 sm:p-10 rounded-xl border-2 border-[#4C1D95]/25 bg-gradient-to-br from-[#F5F1EA] via-white to-[#F5F1EA]/40 shadow-sm"
      >
        <div className="flex items-center gap-3 text-[#4C1D95]">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="label-uppercase !text-xs tracking-[0.15em] text-[#4C1D95]">
            {generating ? "The Professor is reading the data…" : "Loading The Professor's notes…"}
          </span>
        </div>
        <div className="mt-5 space-y-3">
          <div className="h-4 bg-stone-200 rounded animate-pulse w-full" />
          <div className="h-4 bg-stone-200 rounded animate-pulse w-11/12" />
          <div className="h-4 bg-stone-200 rounded animate-pulse w-4/5" />
          <div className="h-4 bg-stone-200 rounded animate-pulse w-3/4" />
        </div>
      </div>
    );
  }

  if (error && !interpretation) {
    return (
      <div
        data-testid="ai-interpretation-error"
        className="mt-10 p-8 sm:p-10 rounded-xl border-2 border-[#4C1D95]/25 bg-gradient-to-br from-[#F5F1EA] via-white to-[#F5F1EA]/40 shadow-sm"
      >
        <div className="flex items-center gap-2.5 mb-3">
          <GraduationCap className="h-5 w-5 text-[#4C1D95]" strokeWidth={2.25} />
          <span className="label-uppercase !text-xs tracking-[0.15em] text-[#4C1D95]">
            The Professor · At a glance
          </span>
        </div>
        <p className="text-[16px] leading-relaxed text-stone-700">{error}</p>
        <button
          onClick={reload}
          data-testid="ai-interpretation-retry"
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4C1D95] text-white text-sm font-underground-sc tracking-wider hover:bg-[#3B1878] transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Try again
        </button>
      </div>
    );
  }

  if (!interpretation?.summary) return null;

  return (
    <div
      data-testid="ai-interpretation-summary"
      className="mt-10 p-8 sm:p-10 rounded-xl border-2 border-[#4C1D95]/25 bg-gradient-to-br from-[#F5F1EA] via-white to-[#F5F1EA]/40 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <GraduationCap className="h-5 w-5 text-[#4C1D95]" strokeWidth={2.25} />
          <span className="label-uppercase !text-xs tracking-[0.15em] text-[#4C1D95]">
            The Professor · At a glance
          </span>
        </div>
        {isAdmin && (
          <button
            onClick={refresh}
            disabled={refreshing}
            data-testid="ai-interpretation-refresh"
            className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider text-stone-500 hover:text-[#4C1D95] disabled:opacity-50"
            title="Admin — ask The Professor to rewrite"
          >
            {refreshing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            rewrite
          </button>
        )}
      </div>
      <p className="mt-5 text-[19px] sm:text-[20px] leading-[1.65] text-stone-900 font-editorial">
        {_HAS_MD.test(interpretation.summary || "") ? (
          <ReactMarkdown>{interpretation.summary}</ReactMarkdown>
        ) : (
          interpretation.summary
        )}
      </p>
      {inheritedFrom && (
        <div
          data-testid="ai-interpretation-inherited"
          className="mt-4 flex items-start gap-2 rounded-md border border-stone-200 bg-stone-50/70 px-3 py-2 text-[11px] leading-relaxed text-stone-600"
        >
          <GraduationCap className="h-3 w-3 text-stone-500 shrink-0 mt-0.5" />
          <span>
            <span className="font-medium text-stone-700">Shared from a curated sibling course</span>
            {" — "}this HESA-verified course inherits The Professor's overview
            from a hand-curated companion degree in the same subject at this
            university. Verified UCAS code &amp; entry data are from the
            official HESA return.
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Compact Professor's note pinned above a data grid. Rendered inline
 * inside a Section, above the raw DataRows.
 */
// Phrases The Professor uses when he *thinks* a field is missing.
// Kept in sync with `scripts/bust_stale_writeups.py::_STALE_PATTERN`.
const _STALE_PROFESSOR_PATTERN = /(not published|not provided|not listed|not available in (?:this|the) dataset|no (?:tuition|salary|employment|visa|scholarship|module|assessment) (?:data|figure|number|information)|data (?:does not|doesn't) include|(?:were|was|is|are) not (?:provided|listed|published|reported)|details? .* (?:are|is) not published|figures? .* (?:are|is) not published)/i;

/**
 * Compact Professor's note pinned above a data grid. Rendered inline
 * inside a Section, above the raw DataRows.
 *
 * `dataAvailable` (bool, optional): if true and the writeup contains
 * "not published"-style phrasing, we suppress the note entirely so
 * users never see the Professor contradict the panel below him. The
 * nightly buster daemon will regenerate the writeup within 24h; in the
 * meantime the section renders without a Professor paragraph, which
 * is safer than shipping a lie.
 */
export function AIInterpretationNote({ interpretation, sectionKey, degreeId, dataAvailable }) {
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagNote, setFlagNote] = useState("");
  const [flagBusy, setFlagBusy] = useState(false);
  const [flagSent, setFlagSent] = useState(false);
  if (!interpretation) return null;
  const text = interpretation[sectionKey];
  if (!text || typeof text !== "string") return null;
  // Safety net — hide the writeup when it contradicts the data grid
  // rendered right below it (systemic guard from 2026-02-22).
  if (dataAvailable && _STALE_PROFESSOR_PATTERN.test(text)) return null;

  const submitFlag = async () => {
    if (flagNote.trim().length < 5 || !degreeId) return;
    setFlagBusy(true);
    try {
      await api.post("/data-flags", {
        degree_id: degreeId,
        target:    `professor_${sectionKey}`,
        note:      flagNote.trim(),
      });
      setFlagSent(true);
      setFlagNote("");
      setTimeout(() => { setFlagOpen(false); setFlagSent(false); }, 2400);
    } catch (e) {
      // Fail quietly — no toast library imported here; the button will unlock.
    } finally {
      setFlagBusy(false);
    }
  };

  return (
    <div
      data-testid={`ai-note-${sectionKey}`}
      className="mb-6 p-5 sm:p-6 rounded-lg border-l-[3px] border-[#4C1D95]/50 bg-[#F5F1EA]/60"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-4 w-4 text-[#4C1D95]" strokeWidth={2.25} />
          <span className="text-[11px] font-underground-sc tracking-[0.15em] text-[#4C1D95]">
            The Professor
          </span>
        </div>
        {degreeId && !flagOpen && !flagSent && (
          <button
            type="button"
            onClick={() => setFlagOpen(true)}
            data-testid={`ai-note-flag-${sectionKey}`}
            className="text-[10px] font-underground-sc tracking-wider text-stone-500 hover:text-red-700 transition-colors inline-flex items-center gap-1"
            title="Spotted something factually wrong? Tell us."
          >
            <Flag className="h-3 w-3" />
            Flag as wrong
          </button>
        )}
        {flagSent && (
          <span
            data-testid={`ai-note-flag-sent-${sectionKey}`}
            className="text-[10px] font-underground-sc tracking-wider text-emerald-700"
          >
            Thanks — logged for our editor
          </span>
        )}
      </div>
      <div className="text-[17px] sm:text-[18px] leading-[1.6] text-stone-800 font-editorial prose prose-stone max-w-none prose-p:my-2 prose-headings:font-editorial">
        {_HAS_MD.test(text) ? (
          <ReactMarkdown>{text}</ReactMarkdown>
        ) : (
          <p>{text}</p>
        )}
      </div>
      {flagOpen && !flagSent && (
        <div className="mt-4 pt-4 border-t border-stone-300/60" data-testid={`ai-note-flag-form-${sectionKey}`}>
          <label className="block text-[11px] font-underground-sc tracking-wider text-stone-600 mb-1.5">
            What's wrong in this section?
          </label>
          <textarea
            rows={3}
            value={flagNote}
            onChange={(e) => setFlagNote(e.target.value)}
            placeholder="e.g. 'Says tuition is not published but it clearly is on the page' or 'Grade requirement is too low for this course'"
            className="w-full text-sm p-2.5 rounded-md border border-stone-300 focus:border-[#4C1D95] focus:outline-none bg-white"
            data-testid={`ai-note-flag-textarea-${sectionKey}`}
            maxLength={1000}
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={submitFlag}
              disabled={flagBusy || flagNote.trim().length < 5}
              data-testid={`ai-note-flag-submit-${sectionKey}`}
              className="text-[11px] font-underground-sc tracking-wider bg-[#4C1D95] hover:bg-[#3B0764] disabled:bg-stone-300 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-full transition-colors"
            >
              {flagBusy ? "Sending…" : "Send to editor"}
            </button>
            <button
              type="button"
              onClick={() => { setFlagOpen(false); setFlagNote(""); }}
              className="text-[11px] font-underground-sc tracking-wider text-stone-500 hover:text-stone-800 px-3 py-1.5"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
